import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService, AUTH_LOGOUT_BROADCAST_KEY } from '../../../services/auth/authService';
import { teamStorage } from '../../../utils/teamStorage';
import startActivityTimestampLogger, {
  startIdleMonitor,
} from '../../../services/auth/idleTrackerService';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Guards routes that require an active authenticated session.
 *
 * Auth check runs ONCE on mount (empty dependency array).
 * Including `location.pathname` in the deps caused an infinite loop:
 *   no token → <Navigate to="/login"> → pathname changes
 *   → effect re-fires → no token → redirect → ♻️
 *
 * Idle monitoring is initialised once after the first successful auth
 * check. A ref guard (`idleInitialisedRef`) prevents duplicate monitors
 * if React re-renders the component while the user is authenticated.
 *
 * @param children - Protected content rendered for authenticated users
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  /** Composite cleanup function returned by initIdleLogout */
  const stopAllRef = useRef<() => void>(() => { /* no-op until monitors start */ });

  /** Prevents starting duplicate idle monitors across re-renders */
  const idleInitialisedRef = useRef(false);

  /**
   * True when the session ended due to idle timeout.
   * Suppresses the automatic <Navigate to="/login"> redirect so the
   * "session expired" message is displayed instead.
   */
  const suppressRedirectOnIdleRef = useRef(false);

  /**
   * Listens for logout broadcasts from other browser tabs.
   *
   * When Tab A calls authService.logout(), it writes AUTH_LOGOUT_BROADCAST_KEY
   * to localStorage before clearing storage. The browser fires a `storage`
   * event in every OTHER same-origin tab. This handler:
   *  1. Clears this tab's sessionStorage (removes all tokens).
   *  2. Writes `auth_forced_logout = '1'` back into sessionStorage so the
   *     Login page knows to pass `prompt=login` to PingFed, preventing the
   *     active SSO cookie from silently re-authenticating the user.
   *  3. Transitions this tab to the unauthenticated state → redirect to /login.
   *
   * Note: the `storage` event is NOT fired in the tab that made the change,
   * so this handler never triggers in the tab that initiated the logout.
   */
  useEffect(() => {
    const handleCrossTabLogout = (event: StorageEvent): void => {
      if (event.key === AUTH_LOGOUT_BROADCAST_KEY && event.newValue !== null) {
        sessionStorage.clear();
        // This prevents PingFed’s SSO cookie from silently re-authenticating.
        sessionStorage.setItem('auth_forced_logout', '1');
        teamStorage.clearTeamId();
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };

    window.addEventListener('storage', handleCrossTabLogout);
    return () => window.removeEventListener('storage', handleCrossTabLogout);
  }, []);

  useEffect(() => {
    let mounted = true;

    /**
     * Returns a composite stop function to clean up both on unmount
     * or on idle-triggered logout.
     *
     * @param timeoutMs - Inactivity threshold in milliseconds
     */
    const initIdleLogout = (timeoutMs: number): (() => void) => {
      const stopLogger = startActivityTimestampLogger();

      const stopIdle = startIdleMonitor(timeoutMs, async () => {
        console.log('⏰ Idle timeout — logging out');
        suppressRedirectOnIdleRef.current = true;

        try {
          await authService.logout();
        } catch (e) {
          console.error('❌ Idle logout error:', e);
        } finally {
          try { stopLogger(); } catch { /* ignore */ }
          try { stopIdle(); } catch { /* ignore */ }
          idleInitialisedRef.current = false;
          if (mounted) setIsAuthenticated(false);
        }
      });

      return () => {
        try { stopIdle(); } catch { /* ignore */ }
        try { stopLogger(); } catch { /* ignore */ }
        idleInitialisedRef.current = false;
      };
    };

    /**
     * Validates the session against authService exactly once on mount.
     *
     * Reads accessToken from sessionStorage — the same storage location
     * that authService.exchangeCodeForToken and refreshAccessToken write to.
     * Using localStorage here (previous bug) caused a mismatch: the token
     * existed in sessionStorage but the check found nothing in localStorage,
     * producing an infinite auth-check → redirect → auth-check loop.
     */
    const checkAuthentication = async (): Promise<void> => {
      try {
        console.log('🔒 ProtectedRoute: checking auth…');

        // Must match the storage used by authService — sessionStorage
        const accessToken = sessionStorage.getItem('accessToken');

        if (!accessToken) {
          console.log('❌ No access token found');
          if (mounted) setIsAuthenticated(false);
          return;
        }

        const isAuth = await authService.checkAndRefreshAuth();
        if (!mounted) return;

        if (isAuth) {
          console.log('✅ User authenticated');

          // Guard prevents duplicate monitors when component re-renders
          if (!idleInitialisedRef.current) {
            stopAllRef.current = initIdleLogout(60 * 60 * 1000); // 60 minutes
            idleInitialisedRef.current = true;
          }

          setIsAuthenticated(true);
        } else {
          console.log('❌ Auth validation failed — session invalid');
          teamStorage.clearTeamId();
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('❌ Auth check error:', error);
        teamStorage.clearTeamId();
        if (mounted) setIsAuthenticated(false);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    checkAuthentication();

    return () => {
      mounted = false;
      try { stopAllRef.current?.(); } catch { /* ignore */ }
    };

    // Empty deps: auth check runs ONCE on mount.
    // DO NOT add location.pathname — it causes an infinite redirect loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
        }}
      >
        <p>Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (suppressRedirectOnIdleRef.current) {
      return (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <p>Your session expired due to inactivity. Please log in again.</p>
        </div>
      );
    }

    // Persist the intended path so the login page can redirect back after auth.
    // Exception: cross-tab logouts (auth_forced_logout set by the broadcast
    // handler). In that case the user should land on /home after re-login,
    // not be silently returned to the page they were on in the logged-out tab.
    // For all other cases — including opening a page in a new tab via
    // cmd+click — the original path is preserved so the user lands exactly
    // where they intended after authentication.
    const isCrossTabLogout = sessionStorage.getItem('auth_forced_logout') === '1';
    if (!isCrossTabLogout) {
      try {
        const returnTo = `${location.pathname}${location.search}${location.hash}`;
        if (returnTo.startsWith('/')) {
          sessionStorage.setItem('postLoginRedirect', returnTo);
        }
      } catch { /* sessionStorage unavailable — proceed without saving */ }
    }

    console.log('🔴 Redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  console.log('🟢 Rendering protected content');
  return <>{children}</>;
};

export default ProtectedRoute;